import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../utils/LanguageContext';
import { getLocalized } from '../../utils/languageUtils';
import { ROUTES } from '../../routes/path';
import api from '../../services/api';
import '../../styles/checkout.css';

export default function ReviewOrder() {
    const navigate = useNavigate();
    const { t, language } = useLanguage();

    const [cart, setCart] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch user data and cart
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch user info
                const userRes = await api.get('/get/me');
                setUserInfo({
                    fullName: userRes.data?.user?.first_name + " " + userRes.data?.user?.last_name || userRes.data?.name || '',
                    phone: userRes.data?.phoneNumber || '',
                    region: userRes.data?.region || ''
                });

                // Fetch cart
                const cartRes = await api.get('get/my/trees/');
                setCart(cartRes.data || []);
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Calculate totals
    const calculateTotals = () => {
        let subtotal = 0;
        let totalDiscount = 0;
        const discountPercentages = new Set();

        cart.forEach(item => {
            const price = parseFloat(item.product.price) || 0;
            const discount = parseFloat(item.product.discount) || 0;
            const itemSubtotal = price * item.count;
            const itemDiscount = (itemSubtotal * discount) / 100;

            subtotal += itemSubtotal;
            totalDiscount += itemDiscount;

            if (discount > 0) {
                discountPercentages.add(discount);
            }
        });

        const total = subtotal - totalDiscount;

        return {
            subtotal,
            totalDiscount,
            total,
            discountPercentages: Array.from(discountPercentages).sort((a, b) => a - b)
        };
    };

    const { subtotal, totalDiscount, total, discountPercentages } = calculateTotals();

    const handleContinueToPayment = () => {
        // Store order data in sessionStorage to pass to payment page
        sessionStorage.setItem('orderReview', JSON.stringify({
            userInfo,
            cart,
            subtotal,
            totalDiscount,
            total
        }));
        navigate(`/${ROUTES.PAYMENT}`);
    };

    // Loading state
    if (loading) {
        return (
            <div className="checkout-container">
                <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🌳</div>
                    <h2>{t('shop_loading')}</h2>
                </div>
            </div>
        );
    }

    // Empty cart state
    if (!cart || cart.length === 0) {
        return (
            <div className="checkout-empty">
                <div className="empty-icon">🛒</div>
                <h2>{t('cart_empty_title')}</h2>
                <p>{t('cart_empty_text')}</p>
                <button
                    className="btn-back-to-shop"
                    onClick={() => navigate(`/${ROUTES.SHOP}`)}
                >
                    <i className="fas fa-tree"></i> {t('prod_back')}
                </button>
            </div>
        );
    }

    return (
        <div className="checkout-container">
            <div className="checkout-wrapper">
                {/* Form Section */}
                <div className="checkout-form-section">
                    <div className="checkout-header">
                        <h1 className="checkout-title">{t('check_title')} - {t('check_title_review')}</h1>
                        <p className="checkout-subtitle">
                            {t('check_subtitle_review')}
                        </p>
                    </div>

                    {/* User Information - Read Only */}
                    <div className="form-card">
                        <h3 className="card-title">
                            <i className="fas fa-user"></i> {t('check_info_title')}
                        </h3>

                        <div className="info-display-grid">
                            <div className="info-display-item">
                                <label className="info-label">{t('check_label_name')}</label>
                                <div className="info-value">{userInfo?.fullName || 'N/A'}</div>
                            </div>

                            <div className="info-display-item">
                                <label className="info-label">{t('check_label_phone')}</label>
                                <div className="info-value">{userInfo?.phone || 'N/A'}</div>
                            </div>

                            <div className="info-display-item">
                                <label className="info-label">{t('check_label_region')}</label>
                                <div className="info-value">{userInfo?.region || 'N/A'}</div>
                            </div>
                        </div>

                        <div className="info-note">
                            <i className="fas fa-info-circle"></i>
                            <span>{t('check_info_note')}</span>
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="form-card">
                        <h3 className="card-title">
                            <i className="fas fa-shopping-cart"></i> {t('check_items_title')}
                        </h3>

                        <div className="order-items-list">
                            {cart.map(item => {
                                const itemPrice = parseFloat(item.product.price) || 0;
                                const itemDiscount = parseFloat(item.product.discount) || 0;
                                const itemSubtotal = itemPrice * item.count;
                                const itemDiscountAmount = (itemSubtotal * itemDiscount) / 100;
                                const itemTotal = itemSubtotal - itemDiscountAmount;

                                return (
                                    <div key={item.id} className="order-review-item">
                                        <div className="item-image">
                                            {item.product?.tree?.image ? (
                                                <img src={item.product.tree.image} alt="" />
                                            ) : (
                                                <i className="fas fa-tree"></i>
                                            )}
                                        </div>
                                        <div className="item-details">
                                            <h4>{getLocalized(item.product.tree, language, 'name')}</h4>
                                            <p className="item-meta">
                                                {t('check_item_qty')}: {item.count}
                                            </p>
                                            {itemDiscount > 0 && (
                                                <span className="item-discount">{itemDiscount}% {t('check_off')}</span>
                                            )}
                                        </div>
                                        <div className="item-price">
                                            {itemTotal.toLocaleString()} UZS
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            className="btn-back"
                            onClick={() => navigate(`/${ROUTES.CART}`)}
                        >
                            <i className="fas fa-arrow-left"></i>
                            {t('check_btn_back_cart')}
                        </button>
                        <button
                            className="btn-submit"
                            onClick={handleContinueToPayment}
                        >
                            {t('check_btn_continue')}
                            <i className="fas fa-arrow-right"></i>
                        </button>
                    </div>
                </div>

                {/* Summary Section */}
                <div className="checkout-summary-section">
                    <div className="summary-card sticky">
                        <h3 className="summary-title">
                            <i className="fas fa-receipt"></i>
                            {t('cart_summary_title')}
                        </h3>

                        <div className="total-trees-badge">
                            <i className="fas fa-tree"></i>
                            <span>{t('check_total_trees')}: <strong>{cart.reduce((sum, item) => sum + item.count, 0)}</strong></span>
                        </div>

                        <div className="summary-items">
                            {cart.map(item => {
                                const itemPrice = parseFloat(item.product.price) || 0;
                                const itemDiscount = parseFloat(item.product.discount) || 0;
                                const itemSubtotal = itemPrice * item.count;
                                const itemDiscountAmount = (itemSubtotal * itemDiscount) / 100;
                                const itemTotal = itemSubtotal - itemDiscountAmount;

                                return (
                                    <div key={item.id} className="summary-item">
                                        <div className="item-image">
                                            {item.product?.tree?.image ? (
                                                <img src={item.product.tree.image} alt="" />
                                            ) : (
                                                <i className="fas fa-tree"></i>
                                            )}
                                        </div>
                                        <div className="item-details">
                                            <h4>{getLocalized(item.product.tree, language, 'name')}</h4>
                                            <p>× {item.count}</p>
                                            {itemDiscount > 0 && (
                                                <span className="item-discount">{itemDiscount}% {t('check_off')}</span>
                                            )}
                                        </div>
                                        <div className="item-price">
                                            {itemTotal.toLocaleString()} UZS
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="summary-totals">
                            <div className="summary-row">
                                <span>{t('cart_subtotal')}</span>
                                <span>{subtotal.toLocaleString()} UZS</span>
                            </div>

                            {totalDiscount > 0 && (
                                <div className="summary-row discount">
                                    <div className="discount-label-wrapper">
                                        <span>
                                            <i className="fas fa-tag"></i>
                                            {t('cart_discount')}
                                        </span>
                                        <div className="discount-percentages">
                                            {discountPercentages.map((percent, index) => (
                                                <span key={index} className="discount-percent-badge">
                                                    {percent}%
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <span>-{totalDiscount.toLocaleString()} UZS</span>
                                </div>
                            )}

                            <div className="summary-row">
                                <span>
                                    <i className="fas fa-seedling" style={{ marginRight: '6px', color: '#22c55e' }}></i>
                                    {t('cart_service')}
                                </span>
                                <span style={{ color: '#22c55e', fontWeight: 700 }}>{t('cart_free')}</span>
                            </div>

                            <div className="summary-divider"></div>

                            <div className="summary-row total">
                                <span>{t('cart_total')}</span>
                                <span className="total-amount">{total.toLocaleString()} UZS</span>
                            </div>

                            {totalDiscount > 0 && (
                                <div className="summary-savings">
                                    <i className="fas fa-piggy-bank"></i>
                                    <span>{t('cart_you_save')} <strong>{totalDiscount.toLocaleString()} UZS</strong></span>
                                </div>
                            )}
                        </div>

                        <button
                            className="btn-summary-checkout"
                            onClick={handleContinueToPayment}
                        >
                            {t('check_btn_continue')}
                            <i className="fas fa-arrow-right"></i>
                        </button>

                        <div className="security-badges">
                            <div className="badge">
                                <i className="fas fa-shield-alt"></i>
                                <span>{t('check_secure')}</span>
                            </div>
                            <div className="badge">
                                <i className="fas fa-lock"></i>
                                <span>{t('check_ssl')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}